/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { LoggingService, Logger } from 'crimson/lib/logging/logging.service';

export class AppBarMenu {
    id: string;
    item: string;
}

export class LoginData {
    username?: string;
    currentPlanShortName?: string;
    avatar?: string;
    avatarSvg?: string;
}

export class AppBarIcon {
    id?: string;
    icon?: string;
    tooltip?: string;
    end?: boolean;
    text?: string;
    loginData?: LoginData;
    menu?: AppBarMenu[];
    button?: boolean;
    disabled?: boolean;
}

export class AppBarString {
    entry?: string;
    params?: Object;
}

export class AppBarTemplate {
    id: string;
    params?: Object;
}

export class AppBarAllSettings {
    title?: AppBarString;
    active?: boolean;
    defaultNavButton?: AppBarIcon;
    navButton?: AppBarIcon;
    navButtonMenu?: boolean;
    navButtonArrow?: boolean;
    navButtonClose?: boolean;
    navButtonSetToDefault?: boolean;
    icons: AppBarIcon[] = [];
    info?: AppBarString;
    template?: AppBarTemplate;
}

@Injectable({
    providedIn: 'root'
})
export class AppBarService {
    private logger: Logger;
    private actionClicked = new Subject<string>();

    private iconsChanged = new BehaviorSubject<AppBarIcon[]>([]);
    private titleChanged = new BehaviorSubject<AppBarString>({ entry: '', params: {} });
    private infoChanged = new BehaviorSubject<AppBarString>({ entry: '', params: {} });
    private navButtonChanged = new BehaviorSubject<AppBarIcon>({});
    private activeChanged = new BehaviorSubject<boolean>(false);
    private transparentChanged = new BehaviorSubject<boolean>(false);
    private appBarState = new BehaviorSubject<boolean>(true);
    private templateChanged = new BehaviorSubject<AppBarTemplate>({ id: '', params: {} });
    private logoRevealed = new BehaviorSubject<boolean>(false);
    private shrinkNavigation = new BehaviorSubject<boolean>(false);
    private focusMenuButton = new Subject<void>();
    private sidebarOpened = new BehaviorSubject<boolean>(false);

    private iconList: AppBarIcon[] = [];
    private defaultNavButton: AppBarIcon = {};

    actionClicked$ = this.actionClicked.asObservable();
    icons$ = this.iconsChanged.asObservable();
    title$ = this.titleChanged.asObservable();
    info$ = this.infoChanged.asObservable();
    navButton$ = this.navButtonChanged.asObservable();
    active$ = this.activeChanged.asObservable();
    transparent$ = this.transparentChanged.asObservable();
    visible$ = this.appBarState.asObservable();
    template$ = this.templateChanged.asObservable();
    logoRevealed$ = this.logoRevealed.asObservable();
    shrinkNavigation$ = this.shrinkNavigation.asObservable();
    focusMenuButton$ = this.focusMenuButton.asObservable();
    sidebarOpened$ = this.sidebarOpened.asObservable();

    public loggedIn = false;

    constructor(private loggingService: LoggingService) {
        this.logger = this.loggingService.getLogger('AppBar');
        this.logger.info('AppBarService started');
    }

    reset() {
        this.titleChanged.next({ entry: '', params: {} });
        this.infoChanged.next({ entry: '', params: {} });
        this.iconsChanged.next([]);
        this.navButtonChanged.next({});
        this.activeChanged.next(false);
        this.templateChanged.next({ id: '', params: {} });
    }

    getCurrentTitle(): AppBarString {
        return this.titleChanged.getValue();
    }

    getCurrentTemplate(): AppBarTemplate {
        return this.templateChanged.getValue();
    }

    setTitle(title: AppBarString) {
        this.titleChanged.next({ entry: title.entry, params: title.params });
    }

    triggerFocusMenuButton() {
        this.focusMenuButton.next();
    }

    setInfo(info: AppBarString) {
        this.infoChanged.next({ entry: info.entry, params: info.params });
    }

    setDefaultNavButton(navButton: AppBarIcon) {
        this.defaultNavButton = navButton;
    }

    setNavButton(navButton: AppBarIcon) {
        this.navButtonChanged.next(navButton);
    }

    setNavButtonMenu(tooltip?: string) {
        this.setNavButton({ id: 'menu', tooltip: tooltip });
    }

    setNavButtonArrow(tooltip?: string) {
        this.setNavButton({ id: 'arrow_left', tooltip: tooltip });
    }

    setNavButtonClose(tooltip?: string) {
        this.setNavButton({ id: 'close', tooltip: tooltip });
    }

    setNavButtonToDefault() {
        this.setNavButton(this.defaultNavButton);
    }

    setNavButtonEmpty() {
        this.setNavButton({});
    }

    setActive(active: boolean) {
        this.activeChanged.next(active);
    }

    setTransparent(transparent: boolean) {
        this.transparentChanged.next(transparent);
    }

    // a special icon with the id of 'closeIgo' will automatically close igo
    addIcon(icon: AppBarIcon) {
        this.iconList.push(icon);
        this.iconsChanged.next(this.iconList);
    }

    deleteIcon(id: string) {
        const index = this.iconList.findIndex(item => item.id === id);
        if (index !== -1) {
            this.iconList.splice(index, 1);
            this.iconsChanged.next(this.iconList);
        }
    }

    setIcons(icons: AppBarIcon[]) {
        this.iconList = icons.slice();
        this.iconsChanged.next(this.iconList);
    }

    setTemplate(template: AppBarTemplate) {
        this.templateChanged.next(template);
    }

    setShrinkNavigation(enabled: boolean) {
        this.shrinkNavigation.next(enabled);
    }

    setSidebarOpened(opened: boolean) {
        this.sidebarOpened.next(opened);
    }

    setLogoRevealed() {
        this.logoRevealed.next(true);
    }

    setAll(settings: AppBarAllSettings) {
        this.reset();
        if (settings.title) {
            this.setTitle(settings.title);
        }
        if (settings.info) {
            this.setInfo(settings.info);
        }
        this.setActive(settings.active);
        this.setIcons(settings.icons);
        if (settings.template !== undefined) {
            this.setTemplate(settings.template);
        } else {
            this.setTemplate({ id: '' });
        }

        if (settings.defaultNavButton) {
            this.setDefaultNavButton(settings.defaultNavButton);
        }

        if (settings.navButton?.id) {
            this.setNavButton(settings.navButton);
        } else if (settings.navButtonMenu) {
            this.setNavButtonMenu('toolbar.menu');
        } else if (settings.navButtonArrow) {
            this.setNavButtonArrow('common.goBack');
        } else if (settings.navButtonClose) {
            this.setNavButtonClose('common.close');
        } else if (settings.navButtonSetToDefault) {
            this.setNavButtonToDefault();
        }
    }

    clearIcons() {
        this.iconList = [];
        this.iconsChanged.next(this.iconList);
    }

    getIcons(): AppBarIcon[] {
        return this.iconList;
    }

    clickIcon(id: string) {
        if (id === 'closeIgo') {
            this.closeIgo();
        }
        this.actionClicked.next(id);
    }

    clickNav(id: string) {
        this.clickIcon(id);
    }

    closeIgo() {}

    setAppBarState(show: boolean) {
        this.appBarState.next(show);
    }
}
